import {
  isTemplateCompatibleWithProvider,
  platformDefaultTemplate,
  type TemplateId,
} from "./listing-templates";

/**
 * Deterministic template resolution for a single listing.
 *
 * Resolution order (mirrors `resolve_listing_template` RPC in migration 0195):
 *   1. listing override (if present and compatible with the connector's channel)
 *   2. connector/account default (if present and compatible)
 *   3. platform/channel default
 *
 * An assignment that is invalid for the channel (e.g. `marketplace-classic` on a
 * BigCommerce connector) is silently skipped in favour of the next tier — never
 * used — so a stale/foreign assignment cannot corrupt a listing's output.
 * This is the single source of truth the UI preview and the publication
 * pipeline share; the backend RPC enforces the same rules server-side.
 */

export type TemplateResolutionSource =
  | "listing_override"
  | "connector_default"
  | "platform_default";

export interface TemplateResolution {
  templateId: TemplateId | null;
  source: TemplateResolutionSource;
  provider: string | null;
}

export interface TemplateResolutionInput {
  /** channel_listings.listing_template_override */
  listingOverride: string | null | undefined;
  /** connectors.listing_template_id */
  connectorDefault: string | null | undefined;
  /** connectors.provider */
  provider: string | null | undefined;
}

export function resolveTemplateForListing(input: TemplateResolutionInput): TemplateResolution {
  const { listingOverride, connectorDefault, provider } = input;

  if (listingOverride && isTemplateCompatibleWithProvider(listingOverride, provider)) {
    return { templateId: listingOverride as TemplateId, source: "listing_override", provider: provider ?? null };
  }

  if (connectorDefault && isTemplateCompatibleWithProvider(connectorDefault, provider)) {
    return { templateId: connectorDefault as TemplateId, source: "connector_default", provider: provider ?? null };
  }

  const platform = platformDefaultTemplate(provider);
  return { templateId: platform ?? null, source: "platform_default", provider: provider ?? null };
}